'use client';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { imageProps } from '@/lib/seo/media';
import { useTranslations } from 'next-intl';
import type { Partner } from '@/lib/v9/data';
import { StoreBadges } from './AppHandoff';
import { DEFAULT_HOWTO_VIDEO, youtubeId } from '@/lib/v9/youtube';
import { Apple, Close, GooglePlay, Sparkle } from './icons';
import { cx, useEscape, useLang, useScrollLock } from './ui';

const SCREENS = ['/v9/screen-1.webp', '/v9/screen-2.webp', '/v9/screen-3.webp', '/v9/screen-4.webp'];
// "Watch how it works" (design .how__play): shown once the team sets the video link.
const HOWTO_VIDEO = youtubeId(process.env.NEXT_PUBLIC_HOWTO_VIDEO_URL) ?? DEFAULT_HOWTO_VIDEO;

function VideoModal({ id, onClose, closeLabel }: { id: string; onClose: () => void; closeLabel: string }) {
  useScrollLock(true);
  useEscape(onClose);
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[120] grid place-items-center bg-[rgba(20,4,32,.82)] p-4 backdrop-blur-[6px]" onClick={onClose}>
      <div className="relative aspect-video w-[min(100%,960px)] overflow-hidden rounded-[18px] bg-black shadow-[0_0_0_1px_rgba(240,213,140,.6),0_40px_80px_-30px_rgba(0,0,0,.9)]" onClick={(e) => e.stopPropagation()}>
        <iframe src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`} title="Mila" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="absolute inset-0 h-full w-full border-0" />
      </div>
      <button type="button" onClick={onClose} aria-label={closeLabel} className="absolute end-4 top-[calc(14px+env(safe-area-inset-top,0px))] grid h-11 w-11 place-items-center rounded-full bg-white/[.14] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.35)]">
        <Close className="h-5 w-5" />
      </button>
    </div>
  );
}

/** "From idea to booked in four taps": steps either side of a phone that changes screen with the active step. */
export function HowItWorks() {
  const t = useTranslations('v9.home');
  const tc = useTranslations('v9.common');
  const [on, setOn] = useState(0);
  const [video, setVideo] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => setOn((s) => (s + 1) % 4), 3800);
    return () => window.clearInterval(id);
  }, []);
  const step = (i: number) => (
    <li key={i} className="grid grid-cols-[60px_minmax(0,1fr)] items-start gap-4 max-lg:grid-cols-[44px_minmax(0,1fr)] max-lg:gap-3" onMouseEnter={() => setOn(i)}>
      <span className={cx('grid h-[60px] w-[60px] place-items-center rounded-full text-[24px] font-extrabold leading-none transition-[background-color,color,transform] duration-500 ease-mila max-lg:h-11 max-lg:w-11 max-lg:text-[18px]',
        on === i ? 'scale-[1.08] bg-deep text-sun max-lg:scale-100' : 'bg-panel text-deep')}>{i + 1}</span>
      <p className={cx('m-0 font-body text-[clamp(15.5px,1.25vw,18.5px)] leading-[var(--lh-b)] text-deep', on === i && 'font-bold')}>{t(`step${i + 1}` as 'step1')}</p>
    </li>
  );
  return (
    <section id="how-it-works" aria-labelledby="ph-hw" className="grid scroll-mt-[calc(var(--hdr)+8px)] overflow-hidden text-center max-lg:overflow-visible">
      <div className="flex flex-col items-center gap-2.5 pb-[clamp(12px,1.8vw,24px)]">
        <p className="m-0 inline-flex items-center gap-2 text-[13px] font-extrabold text-violet"><Sparkle className="h-3.5 w-3.5 text-gold" />{t('howEyebrow')}</p>
        <h2 id="ph-hw" className="m-0 text-balance text-[clamp(28px,4.4vw,52px)] font-extrabold leading-[1.3] text-deep ltr:tracking-[-.02em]">
          {t('howTitle')} <em className="not-italic text-plum ltr:italic">{t('howTitleAcc')}</em>
        </h2>
        <p className="inline-flex items-center gap-2.5 text-[14.5px] font-bold text-deep/75"><Apple className="h-[18px] w-[18px]" /><GooglePlay className="h-[18px] w-[18px]" />{t('howAvail')}</p>
      </div>
      <div className="grid items-center gap-[clamp(20px,6vw,90px)] text-start [--dw:min(300px,38vw)] lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] max-lg:[--dw:min(240px,64vw)]">
        <ol className="m-0 flex list-none flex-col gap-[clamp(16px,3vw,44px)] p-0 max-lg:order-2">{[0, 1].map(step)}</ol>
        <div className="relative w-[var(--dw)] self-end justify-self-center lg:-mb-14 max-lg:order-1">
          <div className="relative z-[1] aspect-[640/1038] w-full rounded-[calc(var(--dw)*.145)] bg-[#2a1838] p-[calc(var(--dw)*.036)] shadow-[0_50px_80px_-34px_rgba(57,22,77,.6),inset_0_0_0_1.5px_rgba(255,255,255,.1)]">
            <span className="absolute left-1/2 top-[calc(var(--dw)*.062)] z-[2] -ml-[13.5%] h-[calc(var(--dw)*.072)] w-[27%] rounded-full bg-[#190c25]" />
            <div className="relative h-full w-full overflow-hidden rounded-[calc(var(--dw)*.112)] bg-white">
              {SCREENS.map((src, i) => (
                <img key={src} src={src} alt="" loading="lazy" className={cx('absolute inset-0 h-full w-full object-cover object-top transition-[opacity,transform] duration-700 ease-mila', on === i ? 'scale-100 opacity-100' : 'scale-[1.04] opacity-0')} />
              ))}
            </div>
          </div>
          {HOWTO_VIDEO && (
            <a href={`https://www.youtube.com/watch?v=${HOWTO_VIDEO}`} target="_blank" rel="noopener"
              onClick={(e) => { e.preventDefault(); setVideo(true); }}
              className="group absolute start-1/2 z-[3] inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-white/[.96] py-2 pe-4 ps-2 text-[13px] font-extrabold text-deep shadow-[0_0_0_1px_rgba(240,213,140,.9),0_18px_34px_-18px_rgba(20,4,32,.6)] ltr:-translate-x-1/2 rtl:translate-x-1/2 lg:bottom-[14%] max-lg:top-[calc(100%-20px)] max-lg:text-[12.5px]">
              <i className="grid h-[38px] w-[38px] flex-none place-items-center rounded-full bg-plum text-white group-hover:bg-deep">
                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path d="M8.5 5.5v13l10-6.5z" fill="currentColor" /></svg>
              </i>
              {t('watchHow')}
            </a>
          )}
        </div>
        <ol className="m-0 flex list-none flex-col gap-[clamp(16px,3vw,44px)] p-0 max-lg:order-3">{[2, 3].map(step)}</ol>
      </div>
      {video && HOWTO_VIDEO && <VideoModal id={HOWTO_VIDEO} onClose={() => setVideo(false)} closeLabel={tc('close')} />}
    </section>
  );
}

/** Two marquees of the brands' logos. */
export function PartnerLogos({ partners }: { partners: Partner[] }) {
  const t = useTranslations('v9.home');
  const logos = partners;
  if (logos.length < 4) return null;
  const half = Math.ceil(logos.length / 2);
  const rows = [logos.slice(0, half), logos.slice(half).concat(logos.slice(0, Math.max(0, half - (logos.length - half))))];
  return (
    <section id="partners" aria-labelledby="ph-pa" className="grid scroll-mt-[calc(var(--hdr)+8px)] gap-3">
      <h2 id="ph-pa" className="m-0 text-[clamp(20px,2.4vw,26px)] font-extrabold leading-[1.4]">{t('partners')}</h2>
      <div dir="ltr" className="relative z-[2] -mx-[var(--gut)] flex flex-col gap-4 overflow-hidden py-2 before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-[2] before:w-[12vw] before:bg-[linear-gradient(90deg,#FBF8FC,rgba(251,248,252,0))] before:content-[''] after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-[2] after:w-[12vw] after:bg-[linear-gradient(270deg,#FBF8FC,rgba(251,248,252,0))] after:content-['']">
        {rows.map((row, r) => (
          <div key={r} className="overflow-hidden">
            <div className="v9-motion flex w-max" style={{ animation: `v9marquee ${r ? 70 : 60}s linear infinite${r ? ' reverse' : ''}`, ['--d' as string]: 1 }}>
              {[0, 1].map((copy) => (
                <ul key={copy} aria-hidden={copy === 1} className="m-0 flex flex-none list-none p-0">
                  {row.map((b) => (
                    <li key={b.id} className="mx-2 grid h-[clamp(84px,11vw,112px)] w-[clamp(140px,13vw,190px)] place-items-center rounded-[20px] bg-white shadow-[0_1px_0_rgba(57,22,77,.06),0_18px_34px_-24px_rgba(57,22,77,.35)]">
                      {/* Logos are uploaded at any size (one was 1.2 MB): resized to the tile. */}
                      <Image {...imageProps(b.logo)} alt="" width={240} height={160} sizes="120px" className="h-auto max-h-[56%] w-auto max-w-[62%] object-contain" />
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** "Track your booking in the app" card. */
export function GetTheApp() {
  const t = useTranslations('v9.home');
  return (
    <section id="get" aria-labelledby="ph-gt" className="relative grid scroll-mt-[calc(var(--hdr)+8px)] gap-2 overflow-hidden rounded-[28px] p-[clamp(22px,3vw,36px)] text-white shadow-[0_0_0_1px_rgba(240,213,140,.45),0_40px_70px_-50px_rgba(30,10,46,.8)] [--getpad:clamp(22px,3vw,36px)] [background:radial-gradient(120%_90%_at_100%_0%,#8257AE,rgba(130,87,174,0)_60%),linear-gradient(160deg,#6A3A8C,#4A2062_70%)] lg:min-h-[360px] lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:items-center lg:gap-x-8">
      <div className="relative z-[1] grid justify-items-start gap-1.5">
        <p className="m-0 mb-0.5 inline-flex items-center gap-2 text-[13px] font-extrabold text-sun"><Sparkle className="h-3.5 w-3.5" />{t('getEyebrow')}</p>
        <h2 id="ph-gt" className="m-0 text-balance text-[clamp(22px,2.6vw,30px)] font-extrabold leading-[1.4]">{t('getTitle')}</h2>
        <p className="m-0 mt-1 max-w-[34em] font-body text-[14.5px] leading-[1.7] text-white/[.85]">{t('getSub')}</p>
        <StoreBadges className="mt-3.5" />
      </div>
      <div aria-hidden="true" className="relative -mx-[var(--getpad)] -mb-[var(--getpad)] mt-1 aspect-[16/9] max-h-[260px] before:absolute before:inset-x-[10%] before:-bottom-[30%] before:h-[120%] before:rounded-full before:bg-[radial-gradient(closest-side,rgba(224,182,255,.32),rgba(183,138,240,.12)_55%,rgba(142,79,208,0))] before:content-[''] lg:-my-[var(--getpad)] lg:me-[calc(-1*var(--getpad))] lg:ms-0 lg:aspect-auto lg:max-h-none lg:min-h-[320px] lg:self-stretch">
        <img src="/v9/get-phone.webp" alt="" loading="lazy" className="absolute -bottom-[32%] end-[10%] w-[min(46%,210px)] drop-shadow-[0_24px_34px_rgba(20,4,32,.6)] lg:-bottom-[26%] lg:end-[8%] lg:w-[min(50%,240px)]" />
        <img src="/v9/hero-piece.webp" alt="" loading="lazy" className="absolute bottom-0 start-[6%] h-[96%] w-auto max-w-[54%] object-contain object-bottom drop-shadow-[0_20px_30px_rgba(20,4,32,.5)] ltr:-scale-x-100 lg:start-[4%] lg:h-[92%] lg:max-w-[40%]" />
      </div>
    </section>
  );
}
